// 推廣活動落地頁（Godfather 套路，通用化版）——/godfather（默認首個上架活動）或 /godfather/:key
// 漏斗 events：campaign_view → campaign_form_start → campaign_submit(status)
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Gift, CheckCircle2, Loader2, Truck, MessageCircle, Radio, BellRing, ShoppingCart, PackageCheck } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { trackPageView, trackEvent, flushEvents } from '../lib/analytics';
import { apiFetchJson } from '../lib/api';
import { useCMS } from '../hooks/useCMS';

interface LeadCampaign {
  id: number;
  key: string;
  name: string;
  headline: string;
  subline: string;
  benefits: string[];
  liveAt?: number | null;
}

// 無法取得活動配置時的後備文案（保持頁面永不白屏；語境＝9.15 直播限定）
const FALLBACK: LeadCampaign = {
  id: 0, key: 'fb-group', name: '9.15 直播限定｜辣中聖品禮盒免費送',
  headline: '免費送你🌶️X-1493 辣中聖品禮盒｜9.15 直播限定',
  subline: '9月15日（二）晚上 8:30 · YYC 香港信任直播間——登記 WhatsApp，直播當晚購買任何產品，禮盒免費寄给你',
  benefits: ['🌶️ X-1493 辣中聖品禮盒（10包享辣原味＋便攜玻璃樽）免費送', '✅ 通過 ALS 食安檢測・香港製造', '登記零成本——直播當晚買任何產品就係你嘅'],
  liveAt: 1789475400,
};

const REGIONS = [
  { value: 'hk', label: '香港島／九龍／新界' },
  { value: 'nt', label: '新界偏遠（東涌、馬灣等）' },
];

export default function Godfather() {
  const navigate = useNavigate();
  const { key: keyParam } = useParams();
  const [params] = useSearchParams();
  const { getValue } = useCMS();
  const [campaign, setCampaign] = useState<LeadCampaign>(FALLBACK);
  const [campaignLoaded, setCampaignLoaded] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [region, setRegion] = useState('hk');
  const [consent, setConsent] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ promo: string; referralCode: string } | null>(null);
  const formStartedRef = useRef(false);
  const viewTrackedRef = useRef(false);

  useEffect(() => {
    trackPageView('/godfather');
    if (params.get('utm_source') || params.get('r')) {
      trackEvent('session_entry', { landing: '/godfather', campaign: keyParam || FALLBACK.key });
      flushEvents();
    }
    // 載入活動配置（失敗用後備文案，仍可提交——後端兼容無 campaignKey）
    apiFetchJson<LeadCampaign>(`/public/lead-campaigns/${encodeURIComponent(keyParam || FALLBACK.key)}`)
      .then((res) => {
        if (res.success && res.data) setCampaign({ ...FALLBACK, ...res.data });
        setCampaignLoaded(true);
      })
      .catch(() => setCampaignLoaded(true));
  }, [keyParam]);

  // 漏斗：view（配置載入後記一次，帶實際 campaignKey）
  useEffect(() => {
    if (campaignLoaded && !viewTrackedRef.current) {
      viewTrackedRef.current = true;
      trackEvent('campaign_view', { campaignKey: campaign.key });
      flushEvents();
    }
  }, [campaignLoaded]);

  // 漏斗：form_start（首次輸入）
  const markFormStart = () => {
    if (formStartedRef.current) return;
    formStartedRef.current = true;
    trackEvent('campaign_form_start', { campaignKey: campaign.key });
  };

  // 直播語境：活動設 live_at → 顯示時間軸（日期以 HKT 顯示）
  const liveAt = campaign.liveAt && campaign.liveAt > 0 ? campaign.liveAt : null;
  const fbUrl = getValue('live.fb_url', 'https://www.facebook.com/profile.php?id=61573736784763');
  const liveDateLabel = liveAt
    ? new Date(liveAt * 1000).toLocaleDateString('zh-HK', { timeZone: 'Asia/Hong_Kong', month: 'numeric', day: 'numeric', weekday: 'short' })
    : '';

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!/^[0-9]{8}$/.test(phone.replace(/\D/g, ''))) {
      setError('請輸入 8 位香港 WhatsApp 號碼');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch('/api/public/godfather/join', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json; charset=utf-8' },
        body: JSON.stringify({ name, phone: `852${phone.replace(/\D/g, '')}`, address, region, campaignKey: campaign.key, marketingConsent: consent }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = data.error || data.detail || '提交失敗，請稍後再試';
        const status = res.status === 409 ? 'dup_phone' : res.status === 429 ? 'dup_ip' : /結束|不存在/.test(msg) ? 'campaign_ended' : 'error';
        trackEvent('campaign_submit', { campaignKey: campaign.key, status });
        flushEvents();
        throw new Error(msg);
      }
      trackEvent('campaign_submit', { campaignKey: campaign.key, status: 'ok' });
      trackEvent('godfather_join', { source: 'fb_group', campaignKey: campaign.key });
      flushEvents();
      setDone({ promo: data.promo || data.data?.promo || '', referralCode: data.referralCode || data.data?.referralCode || '' });
    } catch (e: any) {
      setError(e.message || '提交失敗，請稍後再試');
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "w-full px-4 py-3 border border-stone-300 rounded-xl focus:ring-2 focus:ring-x1493-primary outline-none bg-white text-stone-800";

  return (
    <div className="min-h-screen bg-stone-50">
      <Header />
      <section className="py-10 md:py-16">
        <div className="max-w-lg mx-auto px-4">
          {!done ? (
            <>
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Gift size={32} className="text-x1493-primary" />
                </div>
                <h1 className="text-3xl font-bold mb-2 text-stone-900">{campaign.headline || campaign.name}</h1>
                <p className="text-stone-600">{campaign.subline}</p>
              </div>

              {campaign.benefits.length > 0 && (
                <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6 mb-6 space-y-2.5">
                  {[
                    { icon: <Truck size={18} />, text: campaign.benefits[0] },
                    { icon: <Gift size={18} />, text: campaign.benefits[1] },
                    { icon: <MessageCircle size={18} />, text: campaign.benefits[2] },
                  ].filter((b) => b.text).map((b, i) => (
                    <div key={i} className="flex items-center gap-3 text-stone-700 text-sm">
                      <span className="text-x1493-primary">{b.icon}</span>
                      {b.text}
                    </div>
                  ))}
                </div>
              )}

              {liveAt && (
                <div className="rounded-2xl p-6 mb-6 text-white" style={{ background: 'linear-gradient(120deg, #0b3527 0%, #12503c 60%, #1a6b50 100%)' }}>
                  <div className="flex items-center gap-2 mb-4">
                    <Radio size={20} className="text-x1493-secondary" />
                    <h2 className="font-bold text-lg">{liveDateLabel} · 8:30pm FB 直播</h2>
                  </div>
                  <div className="space-y-3">
                    {[
                      { icon: <BellRing size={16} />, time: '8:00pm', text: 'WhatsApp 提醒你開播（登記咗自動收到）' },
                      { icon: <Radio size={16} />, time: '8:30pm', text: 'YYC 直播間開播——坤哥真人試貨' },
                      { icon: <ShoppingCart size={16} />, time: '即晚', text: '落單任何產品，報碼核銷禮品' },
                      { icon: <PackageCheck size={16} />, time: '之後', text: '🌶️ 辣中聖品禮盒免費寄到你屋企' },
                    ].map((s, i) => (
                      <div key={i} className="flex items-center gap-3 text-sm">
                        <span className="w-16 shrink-0 font-mono font-bold text-x1493-secondary">{s.time}</span>
                        <span className="text-white/90">{s.icon}</span>
                        <span className="text-white/85">{s.text}</span>
                      </div>
                    ))}
                  </div>
                  <a href={fbUrl} target="_blank" rel="noopener noreferrer"
                    className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-x1493-secondary text-x1493-primary font-bold px-6 py-3 rounded-xl hover:brightness-105 transition">
                    🔴 直播間連結（開播撳呢個）
                  </a>
                </div>
              )}

              <form onSubmit={submit} onFocus={markFormStart} className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">姓名 *</label>
                  <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} placeholder="收件人姓名" className={inputCls} required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">WhatsApp 號碼（含國家碼）*</label>
                  <div className="flex items-center">
                    <span className="px-3 py-3 border border-r-0 border-stone-300 rounded-l-xl bg-stone-100 text-stone-500 text-sm">+</span>
                    <input
                      type="tel"
                      inputMode="numeric"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      maxLength={15}
                      placeholder="8529XXXXXXX（海外號碼請含國家碼）"
                      className={`${inputCls} rounded-l-none`}
                      required
                    />
                  </div>
                  <p className="text-xs text-stone-400 mt-1">例：香港 8529xxxxxxx｜英國 447xxxxxxxxx</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">地區</label>
                  <select value={region} onChange={(e) => setRegion(e.target.value)} className={inputCls}>
                    {REGIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">完整郵寄地址 *<span className="text-xs text-stone-400 ml-1">（大廈／村屋請寫詳細）</span></label>
                  <textarea value={address} onChange={(e) => setAddress(e.target.value)} rows={3} maxLength={200}
                    placeholder="例：九龍旺角彌敦道 123 號幸福大廈 12 樓 A 室" className={inputCls} required />
                </div>
                {error && <div className="bg-red-50 text-red-700 p-3 rounded-xl text-sm">{error}</div>}
                <label className="flex items-start gap-2 text-xs text-stone-500 cursor-pointer">
                  <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 w-4 h-4 accent-red-600 flex-shrink-0" />
                  <span>同意本店透過 WhatsApp 發送優惠及會員通訊（可隨時回覆拒收）</span>
                </label>
                <button type="submit" disabled={submitting}
                  className="w-full bg-x1493-primary text-white py-4 rounded-xl font-bold text-lg hover:bg-red-700 transition disabled:opacity-60 flex items-center justify-center gap-2">
                  {submitting ? <Loader2 size={20} className="animate-spin" /> : <Gift size={20} />}
                  {submitting ? '領取中…' : liveAt ? '登記 WhatsApp・鎖定免費禮盒' : '立即領取'}
                </button>
                <p className="text-xs text-stone-400 text-center">每個號碼／網絡限領一次 · 資料只用於{liveAt ? '本次直播禮遇登記' : '本次禮遇郵寄'}</p>
              </form>
            </>
          ) : (
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 size={48} className="text-green-600" />
              </div>
              <h1 className="text-3xl font-bold mb-3">{liveAt ? '登記成功！🎉' : '領取成功！🎉'}</h1>
              <p className="text-stone-600 mb-6">{name}，{liveAt ? `你嘅直播禮遇已鎖定——${liveDateLabel} 8:30pm 見！` : '你嘅禮遇已啟動！'}</p>
              <div className="bg-white rounded-2xl border border-stone-200 p-6 space-y-4 text-left mb-6">
                <div className="border-b border-stone-100 pb-4">
                  <p className="text-sm text-stone-500 mb-1">📋 {liveAt ? '直播限定碼' : '你的專屬折扣碼'}</p>
                  <p className="text-2xl font-mono font-bold text-x1493-primary">{done.promo || '已發送到 WhatsApp'}</p>
                  <p className="text-xs text-stone-400 mt-1">{liveAt ? '直播當晚落單任何產品時報碼，禮盒免費寄給你' : '結帳時輸入即享折扣'}</p>
                </div>
                {liveAt ? (
                  <div className="border-b border-stone-100 pb-4">
                    <p className="text-sm text-stone-500 mb-1">⏰ 開播前半個鐘</p>
                    <p className="text-sm text-stone-700">我哋會喺 WhatsApp 提醒你開播——記得開返通知，咪行開喇！</p>
                  </div>
                ) : (
                  <div className="border-b border-stone-100 pb-4">
                    <p className="text-sm text-stone-500 mb-1">📮 試吃裝將寄出至</p>
                    <p className="font-medium text-stone-800">{address}</p>
                    <p className="text-xs text-stone-400 mt-1">3-5 個工作天內出貨</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-stone-500 mb-1">📲 完整確認訊息</p>
                  <p className="text-sm text-stone-700">已發送到你的 WhatsApp（+852{phone.replace(/\D/g, '')}），記得留意訊息！</p>
                </div>
              </div>
              <button onClick={() => navigate('/store')}
                className="w-full bg-x1493-primary text-white py-4 rounded-xl font-bold text-lg hover:bg-red-700 transition flex items-center justify-center gap-2">
                <Gift size={20} /> 用折扣碼即刻去行街
              </button>
              <button onClick={() => navigate('/')} className="mt-3 text-stone-500 hover:text-stone-700 text-sm underline">返回首頁</button>
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
